import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { FaArrowLeft, FaArrowUp, FaCircle, FaDoorOpen } from "react-icons/fa";
import { debounce } from "lodash";
import { ChatMessage } from "server/src/clientExports";
import cx from "classnames";
import { AiOutlineClose, AiOutlineSetting } from "react-icons/ai";
import { ImExit } from "react-icons/im";

interface P {
  userId?: string;
  messages: ChatMessage[];
  sendMessage: () => void;
  currentMessage: string;
  loggedIn: boolean;
  setCurrentMessage: (value: string) => void;
  goToSettings: () => void;
  closeChat: () => void;
}

export const ChatBox = ({
  messages,
  sendMessage,
  currentMessage,
  userId,
  loggedIn,
  setCurrentMessage,
  goToSettings,
  closeChat
}: P) => {
  const [watchingForMessages, setWatchingForMessages] = useState(true);
  const [chatLength, setChatLength] = useState(0);
  const [chatElement, setChatElement] = useState<HTMLDivElement>();
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      sendMessage();
      chatElement?.scrollTo(0, 999999);
    },
    [sendMessage, chatElement]
  );

  // TODO when incoming message is mine, scroll to bottom directly
  const handleChatScroll = useCallback(
    debounce(
      () => {
        // console.log("Scroll called");
        const element = chatElement;
        if (!element) {
          return;
        }
        const scrolledToBottom = element.scrollTop === element.scrollHeight - element.offsetHeight;
        if (scrolledToBottom) {
          // console.log("Watching for message: true");
          setWatchingForMessages(true);
        } else {
          // console.log("Watching for message: false");
          setWatchingForMessages(false);
        }
      },
      300,
      {
        leading: true
      }
    ),
    [chatElement]
  );

  useEffect(() => {
    // if messages are not defined
    if (!messages || messages.length === 0) {
      return;
    }

    const lastMessageMine = messages[messages.length - 1].senderId === userId;

    // if not watching for messages
    if (!lastMessageMine && !watchingForMessages) {
      return;
    }

    // if new messages came in or I sent the last message
    if (lastMessageMine || messages.length > chatLength) {
      // scroll to bottom
      chatElement?.scrollTo(0, 999999);

      setChatLength(messages.length);
    }
  }, [messages, chatLength, watchingForMessages, userId, chatElement]);
  return (
    <div className="flex flex-col w-full h-full">
      <div className="h-9 mt-1 mb-1 flex flex-row justify-between items-center border-b-[1px]">
        <div className="pl-2" onClick={goToSettings}>
          {loggedIn && (
            <div className="cursor-pointer bg-black px-1 py-1 rounded-full">
              <ImExit color="white" fontSize={14} />
            </div>
          )}
        </div>
        <div className="font-bold">Chat Radio OM Bun - Prietenul tau</div>
        <div className="pr-2">
          <div className="cursor-pointer bg-black px-1 py-1 rounded-full" onClick={closeChat}>
            <AiOutlineClose color="white" fontSize={14} />
          </div>
        </div>
      </div>

      <div
        ref={setChatElement as any}
        className="flex flex-col w-full h-full overflow-y-auto overflow-x-clip items-stretch mt-1 px-2 pt-1"
        onScroll={handleChatScroll}>
        {messages.map((msg, idx) => {
          const messageBefore = idx > 0 ? messages[idx - 1] : undefined;
          const nextMessage = idx < messages.length - 1 ? messages[idx + 1] : undefined;
          let showName = true;
          const messageBeforeSameSender =
            messageBefore?.senderId === msg.senderId && messageBefore.sender === msg.sender;
          const messagerAfterSameSender =
            nextMessage?.senderId === msg.senderId && nextMessage.sender === msg.sender;

          if (messageBeforeSameSender) {
            showName = false;
          }
          const nameColor = NAME_COLORS[idx % 7];
          return (
            <div
              key={msg.id}
              className={cx("", {
                "mb-2": messageBeforeSameSender && !messagerAfterSameSender
              })}>
              <div className="break-words whitespace-pre-wrap mb-1">
                <span
                  className={cx("font-bold", { hidden: !showName })}
                  style={{ color: nameColor }}>
                  {msg.sender}
                </span>
                <span className={cx({ hidden: !showName })}>{":"}&nbsp;</span>
                <span>{msg.message}</span>
              </div>
            </div>
          );
        })}
      </div>
      <form className="w-full px-2 flex flex-row mt-2" onSubmit={handleFormSubmit}>
        <input
          className="flex-1 px-2"
          value={currentMessage}
          onChange={(e) => setCurrentMessage(e.target.value)}
          placeholder="Scrie un mesaj"
          type="text"></input>
        <button className="bg-a-purple px-4 py-2" type="submit">
          <FaArrowUp color="white" />
        </button>
      </form>
      <div className="w-full mt-2" />
    </div>
  );
};

const NAME_COLORS: { [key: number]: string } = {
  0: "#fcba03",
  1: "#4ade10",
  2: "#1bc4a5",
  3: "#205abd",
  4: "#8528b8",
  5: "#b82892",
  6: "#d91838"
};
